import { useMemo } from "react";
import ReportsWorkspace from "@/components/feature/reports/ReportsWorkspace";
import type {
  ReportFilters,
  ReportOptions,
  ReportResult,
  ReportRow,
  ReportTotal,
  StatutFilterConfig,
} from "@/components/feature/reports/types";
import { inDateRange } from "@/components/feature/reports/utils";
import { usePlatformData } from "@/store/platform/PlatformDataProvider";
import {
  PLATFORM_CONTRAT_STATUT_LABELS,
  platformContratReference,
  platformLocataireNomComplet,
  type PlatformBien,
  type PlatformContrat,
  type PlatformImmeuble,
  type PlatformLocataire,
} from "@/store/platform/types";
import { useBilling } from "@/store/billing/BillingProvider";
import {
  ABONNEMENT_STATUT_LABELS,
  abonnementReference,
  PAIEMENT_METHODE_LABELS,
  PAIEMENT_STATUT_LABELS,
  paiementReference,
  type PlatformAbonnement,
  type PlatformPaiement,
} from "@/store/billing/types";
import { useFormules } from "@/store/formules/FormulesProvider";
import { useTerritory } from "@/store/territory/TerritoryProvider";
import { useSuperAdmin } from "@/pages/super-admin/data/SuperAdminProvider";
import {
  COMPTE_STATUT_LABELS,
  COMPTE_STATUT_OPTIONS,
  COMPTE_TYPE_CONFIG,
  utilisateurRoleLabel,
  type Compte,
} from "@/pages/super-admin/data/types";
import { BIEN_STATUT_LABELS, BIEN_TYPE_LABELS, type BienStatut, type BienType } from "@/pages/proprietaire/biens/types";
import { formatDate, formatFcfa } from "@/utils/format";
import { SUPER_ADMIN_REPORT_TYPES } from "./catalog";

interface Ctx {
  comptes: Compte[];
  immeubles: PlatformImmeuble[];
  biens: PlatformBien[];
  locataires: PlatformLocataire[];
  contrats: PlatformContrat[];
  abonnements: PlatformAbonnement[];
  paiements: PlatformPaiement[];
  formuleNom: (id: string) => string;
  regionName: (id: string) => string;
  provinceName: (id: string) => string;
  communeName: (id: string) => string;
  provincesOf: (regionId: string) => { id: string }[];
  communesOf: (provinceId: string) => { id: string }[];
  secteurOf: (communeId: string) => { id: string }[];
  communesOfRegion: (regionId: string) => { id: string }[];
  secteursOfRegion: (regionId: string) => { id: string }[];
  regions: { id: string; nom: string }[];
}

const OPT = (value: string, label: string) => ({ value, label });
const total = (label: string, value: string, tone: ReportTotal["tone"] = "neutral"): ReportTotal => ({ label, value, tone });
const sumFcfa = (values: number[]) => formatFcfa(values.reduce((s, v) => s + v, 0));

const immeubleNom = (ctx: Ctx, id: string) => ctx.immeubles.find((i) => i.id === id)?.nom ?? "—";
const bienLabel = (ctx: Ctx, id: string) => {
  const bien = ctx.biens.find((b) => b.id === id);
  return bien ? `${BIEN_TYPE_LABELS[bien.type as BienType]}${bien.reference ? ` ${bien.reference}` : ""}` : "—";
};
const locataireNom = (ctx: Ctx, id: string) => {
  const loc = ctx.locataires.find((l) => l.id === id);
  return loc ? platformLocataireNomComplet(loc) : "—";
};

function reportUtilisateurs(ctx: Ctx, f: ReportFilters): ReportResult {
  const rows = ctx.comptes
    .filter((c) => !f.statut || c.statut === f.statut)
    .filter((c) => inDateRange(c.createdAt, f.dateFrom, f.dateTo))
    .map<ReportRow>((c) => ({
      nom: c.nom,
      type: COMPTE_TYPE_CONFIG[c.type].singular,
      email: c.email || "—",
      telephone: c.telephone || "—",
      ville: c.ville || "—",
      role: c.type === "utilisateur" ? utilisateurRoleLabel(c.role) : "—",
      formule: ctx.formuleNom(c.formuleId),
      statut: COMPTE_STATUT_LABELS[c.statut],
    }));
  return {
    title: "Rapport des utilisateurs",
    description: "Comptes de la plateforme par type, ville, formule et statut.",
    columns: [
      { key: "nom", header: "Compte" },
      { key: "type", header: "Type" },
      { key: "email", header: "Email" },
      { key: "telephone", header: "Téléphone" },
      { key: "ville", header: "Ville" },
      { key: "role", header: "Rôle" },
      { key: "formule", header: "Formule" },
      { key: "statut", header: "Statut" },
    ],
    rows,
    totals: [
      total("Comptes", String(rows.length), "primary"),
      total("Actifs", String(ctx.comptes.filter((c) => c.statut === "actif").length), "secondary"),
      total("Propriétaires", String(ctx.comptes.filter((c) => c.type === "proprietaire").length), "accent"),
      total("Agences", String(ctx.comptes.filter((c) => c.type === "agence").length), "neutral"),
    ],
  };
}

function reportComptesParType(ctx: Ctx, f: ReportFilters, type: "proprietaire" | "agence"): ReportResult {
  const comptes = ctx.comptes.filter((c) => c.type === type);
  const rows = comptes
    .filter((c) => !f.statut || c.statut === f.statut)
    .filter((c) => inDateRange(c.createdAt, f.dateFrom, f.dateTo))
    .map<ReportRow>((c) => ({
      nom: c.nom,
      responsable: c.responsable || "—",
      email: c.email || "—",
      telephone: c.telephone || "—",
      ville: c.ville || "—",
      formule: ctx.formuleNom(c.formuleId),
      immeubles: ctx.immeubles.filter((i) => i.scope === (type === "proprietaire" ? "proprietaire" : "agence")).length,
      statut: COMPTE_STATUT_LABELS[c.statut],
    }));
  const label = COMPTE_TYPE_CONFIG[type].label;
  return {
    title: `Rapport des ${label.toLowerCase()}`,
    description: `Comptes ${label.toLowerCase()} de la plateforme.`,
    columns: [
      { key: "nom", header: type === "proprietaire" ? "Propriétaire" : "Agence" },
      { key: "responsable", header: "Responsable" },
      { key: "email", header: "Email" },
      { key: "telephone", header: "Téléphone" },
      { key: "ville", header: "Ville" },
      { key: "formule", header: "Formule" },
      { key: "statut", header: "Statut" },
    ],
    rows,
    totals: [
      total(label, String(rows.length), "primary"),
      total("Actifs", String(comptes.filter((c) => c.statut === "actif").length), "secondary"),
    ],
  };
}

function reportImmeubles(ctx: Ctx, f: ReportFilters): ReportResult {
  const rows = ctx.immeubles
    .filter((i) => !f.immeubleId || i.id === f.immeubleId)
    .filter((i) => inDateRange(i.createdAt, f.dateFrom, f.dateTo))
    .map<ReportRow>((i) => ({
      nom: i.nom,
      espace: i.scope === "agence" ? "Espace Agence" : "Espace Propriétaire",
      region: i.region || "—",
      province: i.province || "—",
      commune: i.commune || "—",
      secteur: i.secteur || "—",
      biens: ctx.biens.filter((b) => b.immeubleId === i.id).length,
      contrats: ctx.contrats.filter((c) => c.immeubleId === i.id).length,
    }));
  return {
    title: "Rapport des immeubles",
    description: "Immeubles de la plateforme avec localisation et rattachements.",
    columns: [
      { key: "nom", header: "Immeuble" },
      { key: "espace", header: "Espace" },
      { key: "region", header: "Région" },
      { key: "province", header: "Province" },
      { key: "commune", header: "Commune / Ville" },
      { key: "secteur", header: "Secteur / Quartier" },
      { key: "biens", header: "Biens", align: "right" },
      { key: "contrats", header: "Contrats", align: "right" },
    ],
    rows,
    totals: [
      total("Immeubles", String(rows.length), "primary"),
      total("Biens rattachés", String(ctx.biens.filter((b) => !f.immeubleId || b.immeubleId === f.immeubleId).length), "secondary"),
    ],
  };
}

function reportBiens(ctx: Ctx, f: ReportFilters): ReportResult {
  const rows = ctx.biens
    .filter((b) => !f.immeubleId || b.immeubleId === f.immeubleId)
    .filter((b) => !f.bienId || b.id === f.bienId)
    .filter((b) => !f.statut || b.statut === f.statut)
    .filter((b) => inDateRange(b.createdAt, f.dateFrom, f.dateTo))
    .map<ReportRow>((b) => ({
      type: BIEN_TYPE_LABELS[b.type as BienType],
      reference: b.reference || "—",
      immeuble: immeubleNom(ctx, b.immeubleId),
      espace: b.scope === "agence" ? "Espace Agence" : "Espace Propriétaire",
      loyer: formatFcfa(b.loyerMensuel),
      statut: BIEN_STATUT_LABELS[b.statut as BienStatut],
    }));
  return {
    title: "Rapport des biens",
    description: "Biens de la plateforme par type, immeuble et statut.",
    columns: [
      { key: "type", header: "Type" },
      { key: "reference", header: "Référence" },
      { key: "immeuble", header: "Immeuble" },
      { key: "espace", header: "Espace" },
      { key: "loyer", header: "Loyer mensuel", align: "right" },
      { key: "statut", header: "Statut" },
    ],
    rows,
    totals: [
      total("Biens", String(rows.length), "primary"),
      total("Loyer mensuel cumulé", sumFcfa(ctx.biens.filter((b) => !f.immeubleId || b.immeubleId === f.immeubleId).map((b) => b.loyerMensuel)), "secondary"),
    ],
  };
}

function reportLocataires(ctx: Ctx, f: ReportFilters): ReportResult {
  const rows = ctx.locataires
    .filter((l) => !f.locataireId || l.id === f.locataireId)
    .filter((l) => {
      if (!f.immeubleId && !f.bienId) return true;
      return ctx.contrats.some((c) => c.locataireId === l.id && (!f.immeubleId || c.immeubleId === f.immeubleId) && (!f.bienId || c.bienId === f.bienId));
    })
    .filter((l) => inDateRange(l.createdAt, f.dateFrom, f.dateTo))
    .map<ReportRow>((l) => {
      const contrat = ctx.contrats.find((c) => c.locataireId === l.id);
      return {
        nom: platformLocataireNomComplet(l),
        telephone: l.telephone || "—",
        email: l.email || "—",
        profession: l.profession || "—",
        espace: l.scope === "agence" ? "Espace Agence" : "Espace Propriétaire",
        logement: contrat ? bienLabel(ctx, contrat.bienId) : "—",
        contrats: ctx.contrats.filter((c) => c.locataireId === l.id).length,
      };
    });
  return {
    title: "Rapport des locataires",
    description: "Répertoire des locataires et de leur logement.",
    columns: [
      { key: "nom", header: "Locataire" },
      { key: "telephone", header: "Téléphone" },
      { key: "email", header: "Email" },
      { key: "profession", header: "Profession" },
      { key: "logement", header: "Logement" },
      { key: "contrats", header: "Contrats", align: "right" },
    ],
    rows,
    totals: [total("Locataires", String(rows.length), "primary")],
  };
}

function reportContrats(ctx: Ctx, f: ReportFilters): ReportResult {
  const rows = ctx.contrats
    .filter((c) => !f.immeubleId || c.immeubleId === f.immeubleId)
    .filter((c) => !f.bienId || c.bienId === f.bienId)
    .filter((c) => !f.locataireId || c.locataireId === f.locataireId)
    .filter((c) => !f.statut || c.statut === f.statut)
    .filter((c) => inDateRange(c.dateDebut, f.dateFrom, f.dateTo))
    .map<ReportRow>((c) => ({
      reference: platformContratReference(c.id),
      immeuble: immeubleNom(ctx, c.immeubleId),
      bien: bienLabel(ctx, c.bienId),
      locataire: locataireNom(ctx, c.locataireId),
      debut: c.dateDebut ? formatDate(c.dateDebut) : "—",
      fin: c.dateFin ? formatDate(c.dateFin) : "—",
      loyer: formatFcfa(c.loyerMensuel),
      caution: formatFcfa(c.caution),
      statut: PLATFORM_CONTRAT_STATUT_LABELS[c.statut],
    }));
  return {
    title: "Rapport des contrats",
    description: "Contrats de la plateforme par période, loyer et statut.",
    columns: [
      { key: "reference", header: "Référence" },
      { key: "immeuble", header: "Immeuble" },
      { key: "bien", header: "Bien" },
      { key: "locataire", header: "Locataire" },
      { key: "debut", header: "Début" },
      { key: "fin", header: "Fin" },
      { key: "loyer", header: "Loyer", align: "right" },
      { key: "caution", header: "Caution", align: "right" },
      { key: "statut", header: "Statut" },
    ],
    rows,
    totals: [
      total("Contrats", String(rows.length), "primary"),
      total("Loyer mensuel cumulé", sumFcfa(ctx.contrats.filter((c) => (!f.immeubleId || c.immeubleId === f.immeubleId) && (!f.bienId || c.bienId === f.bienId)).map((c) => c.loyerMensuel)), "secondary"),
    ],
  };
}

function reportAbonnements(ctx: Ctx, f: ReportFilters): ReportResult {
  const rows = ctx.abonnements
    .filter((a) => !f.statut || a.statut === f.statut)
    .filter((a) => inDateRange(a.dateDebut || a.createdAt, f.dateFrom, f.dateTo))
    .map<ReportRow>((a) => ({
      reference: abonnementReference(a.id),
      compte: a.compteNom,
      formule: ctx.formuleNom(a.formuleId),
      periodicite: a.periodicite === "annuel" ? "Annuelle" : "Mensuelle",
      montant: formatFcfa(a.montant),
      debut: a.dateDebut ? formatDate(a.dateDebut) : "—",
      fin: a.dateFin ? formatDate(a.dateFin) : "—",
      statut: ABONNEMENT_STATUT_LABELS[a.statut],
    }));
  return {
    title: "Rapport des abonnements",
    description: "Abonnements à la plateforme par compte, formule et statut.",
    columns: [
      { key: "reference", header: "Référence" },
      { key: "compte", header: "Compte" },
      { key: "formule", header: "Formule" },
      { key: "periodicite", header: "Périodicité" },
      { key: "montant", header: "Montant", align: "right" },
      { key: "debut", header: "Début" },
      { key: "fin", header: "Fin" },
      { key: "statut", header: "Statut" },
    ],
    rows,
    totals: [
      total("Abonnements", String(rows.length), "primary"),
      total("Actifs", String(ctx.abonnements.filter((a) => a.statut === "actif").length), "secondary"),
      total("Montant annuel potentiel", sumFcfa(ctx.abonnements.filter((a) => a.statut === "actif").map((a) => (a.periodicite === "annuel" ? a.montant : a.montant * 12))), "accent"),
    ],
  };
}

function reportPaiements(ctx: Ctx, f: ReportFilters): ReportResult {
  const rows = ctx.paiements
    .filter((p) => !f.statut || p.statut === f.statut)
    .filter((p) => inDateRange(p.date, f.dateFrom, f.dateTo))
    .map<ReportRow>((p) => ({
      reference: p.reference || paiementReference(p.id),
      compte: p.compteNom,
      formule: ctx.formuleNom(p.formuleId),
      montant: formatFcfa(p.montant),
      date: p.date ? formatDate(p.date) : "—",
      methode: PAIEMENT_METHODE_LABELS[p.methode],
      periode: p.periode || "—",
      statut: PAIEMENT_STATUT_LABELS[p.statut],
    }));
  return {
    title: "Rapport des paiements",
    description: "Paiements d'abonnement par date, méthode et statut.",
    columns: [
      { key: "reference", header: "Référence" },
      { key: "compte", header: "Compte" },
      { key: "formule", header: "Formule" },
      { key: "montant", header: "Montant", align: "right" },
      { key: "date", header: "Date" },
      { key: "methode", header: "Méthode" },
      { key: "periode", header: "Période" },
      { key: "statut", header: "Statut" },
    ],
    rows,
    totals: [
      total("Paiements", String(rows.length), "primary"),
      total("Encaissé (validé)", sumFcfa(ctx.paiements.filter((p) => p.statut === "valide").map((p) => p.montant)), "secondary"),
      total("En attente", String(ctx.paiements.filter((p) => p.statut === "en_attente").length), "accent"),
    ],
  };
}

function reportTerritoire(ctx: Ctx, f: ReportFilters): ReportResult {
  const rows = ctx.regions.map<ReportRow>((region) => ({
    region: region.nom,
    provinces: ctx.provincesOf(region.id).length,
    communes: ctx.communesOfRegion(region.id).length,
    secteurs: ctx.secteursOfRegion(region.id).length,
    immeubles: ctx.immeubles.filter((i) => i.region === region.nom).length,
  }));
  return {
    title: "Rapport territorial",
    description: "Découpage territorial et immeubles par région.",
    columns: [
      { key: "region", header: "Région" },
      { key: "provinces", header: "Provinces", align: "right" },
      { key: "communes", header: "Communes / Villes", align: "right" },
      { key: "secteurs", header: "Secteurs / Quartiers", align: "right" },
      { key: "immeubles", header: "Immeubles", align: "right" },
    ],
    rows,
    totals: [
      total("Régions", String(ctx.regions.length), "primary"),
      total("Immeubles localisés", String(ctx.immeubles.filter((i) => i.region).length), "secondary"),
    ],
  };
}

function buildReport(ctx: Ctx, typeId: string, filters: ReportFilters): ReportResult {
  switch (typeId) {
    case "utilisateurs":
      return reportUtilisateurs(ctx, filters);
    case "proprietaires":
      return reportComptesParType(ctx, filters, "proprietaire");
    case "agences":
      return reportComptesParType(ctx, filters, "agence");
    case "immeubles":
      return reportImmeubles(ctx, filters);
    case "biens":
      return reportBiens(ctx, filters);
    case "locataires":
      return reportLocataires(ctx, filters);
    case "contrats":
      return reportContrats(ctx, filters);
    case "abonnements":
      return reportAbonnements(ctx, filters);
    case "paiements":
      return reportPaiements(ctx, filters);
    default:
      return reportTerritoire(ctx, filters);
  }
}

function getStatutConfig(typeId: string): StatutFilterConfig {
  switch (typeId) {
    case "utilisateurs":
    case "proprietaires":
    case "agences":
      return { label: "Statut du compte", options: COMPTE_STATUT_OPTIONS };
    case "biens":
      return { label: "Statut du bien", options: Object.entries(BIEN_STATUT_LABELS).map(([v, l]) => OPT(v, l)) };
    case "contrats":
      return { label: "Statut du contrat", options: Object.entries(PLATFORM_CONTRAT_STATUT_LABELS).map(([v, l]) => OPT(v, l)) };
    case "abonnements":
      return { label: "Statut de l'abonnement", options: Object.entries(ABONNEMENT_STATUT_LABELS).map(([v, l]) => OPT(v, l)) };
    case "paiements":
      return { label: "Statut du paiement", options: Object.entries(PAIEMENT_STATUT_LABELS).map(([v, l]) => OPT(v, l)) };
    default:
      return { label: "Statut", options: [] };
  }
}

export default function SuperAdminRapportsPage() {
  const { comptes } = useSuperAdmin();
  const { immeubles, biens, locataires, contrats } = usePlatformData();
  const { abonnements, paiements } = useBilling();
  const { formules } = useFormules();
  const territory = useTerritory();

  const ctx: Ctx = useMemo(
    () => ({
      comptes,
      immeubles,
      biens,
      locataires,
      contrats,
      abonnements,
      paiements,
      formuleNom: (id: string) => formules.find((f) => f.id === id)?.nom ?? id,
      regionName: territory.regionName,
      provinceName: territory.provinceName,
      communeName: territory.communeName,
      provincesOf: territory.provincesOf,
      communesOf: territory.communesOf,
      secteurOf: territory.secteurOf,
      communesOfRegion: territory.communesOfRegion,
      secteursOfRegion: territory.secteursOfRegion,
      regions: territory.regions,
    }),
    [comptes, immeubles, biens, locataires, contrats, abonnements, paiements, formules, territory]
  );

  const options: ReportOptions = useMemo(
    () => ({
      immeubles: immeubles.map((i) => ({ value: i.id, label: i.nom })),
      biens: biens.map((b) => ({
        value: b.id,
        label: `${BIEN_TYPE_LABELS[b.type as BienType]}${b.reference ? ` ${b.reference}` : ""}`,
        immeubleId: b.immeubleId,
      })),
      locataires: locataires.map((l) => ({ value: l.id, label: platformLocataireNomComplet(l) })),
    }),
    [immeubles, biens, locataires]
  );

  return (
    <ReportsWorkspace
      heading="Rapports"
      intro="Générez, prévisualisez puis téléchargez les rapports de la plateforme. Chaque rapport s'appuie uniquement sur les données réellement présentes dans les stores ; si aucune donnée n'existe, le rapport affiche zéro."
      reportTypes={SUPER_ADMIN_REPORT_TYPES}
      options={options}
      getStatutConfig={getStatutConfig}
      buildReport={(typeId, filters) => buildReport(ctx, typeId, filters)}
      filenamePrefix="rapports-super-admin"
    />
  );
}